export interface UserBundle {
  id: string;
  email: string;
  nombreCompleto: string;
  rol: string;
  organizationId: string;
  bodegaDefaultId?: string;
  permisos: string[];
}

const TOKEN_KEY = 'mibodega-token';
const REFRESH_TOKEN_KEY = 'mibodega-refresh-token';
const USER_KEY = 'mibodega-user';

export function persistUser(user: UserBundle) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getCachedUser(): UserBundle | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserBundle;
  } catch {
    return null;
  }
}

export function decodeJwtPayload(token: string): {
  sub?: string;
  email?: string;
  orgId?: string;
  rol?: string;
  permisos?: string[];
} | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getCurrentUser(): UserBundle | null {
  const cached = getCachedUser();
  const token = localStorage.getItem(TOKEN_KEY);
  const payload = token ? decodeJwtPayload(token) : null;
  if (cached && payload?.rol) {
    return {
      ...cached,
      permisos: payload.permisos ?? cached.permisos,
      rol: payload.rol,
    };
  }
  if (payload?.rol) {
    return {
      id: payload.sub ?? '',
      email: payload.email ?? '',
      nombreCompleto: '',
      rol: payload.rol,
      organizationId: payload.orgId ?? '',
      permisos: payload.permisos ?? [],
    };
  }
  return cached;
}

export function hasPermission(perm: string): boolean {
  const u = getCurrentUser();
  if (!u) return false;
  if (u.rol === 'ADMIN') return true;
  return u.permisos?.includes(perm) ?? false;
}

export function hasAllPermissions(perms: string[]): boolean {
  const u = getCurrentUser();
  if (!u) return false;
  if (u.rol === 'ADMIN') return true;
  return perms.every((p) => u.permisos?.includes(p) ?? false);
}

export function clearUser() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}
